import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { IdOption } from "./interfaces";
import S from "./strings";

interface Props {
	label: string;
	/** Texto del botón y de la primera opción cuando no hay nada elegido ("Todas las Maestras"). */
	allText: string;
	options: IdOption[];
	value: number[];
	onChange: (ids: number[]) => void;
	disabled?: boolean;
	/** "elegidas" (maestras) o "elegidos" (cursos, periodos) para el resumen "3 elegidos". */
	feminine?: boolean;
}

const normalizar = (texto: any): string => String(texto || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Selección múltiple compacta (R2, 2026-10-22): un botón con lo elegido que abre una lista con
 * casillas y buscador. "Todos" vacía la selección. Cabe en 375 px (la lista ocupa el ancho del filtro).
 */
const MultiSelect: React.FC<Props> = ({ label, allText, options, value, onChange, disabled, feminine }) => {
	const [abierto, setAbierto] = useState<boolean>(false);
	const [buscar, setBuscar] = useState<string>("");
	const caja = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!abierto) return;
		const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") setAbierto(false); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("keydown", tecla);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", tecla); };
	}, [abierto]);

	//Al cerrar se olvida lo buscado: la próxima vez se ve la lista completa.
	useEffect(() => { if (!abierto) setBuscar(""); }, [abierto]);

	const visibles = useMemo(() => {
		const q = normalizar(buscar.trim());
		return q ? options.filter(o => normalizar(o.name).includes(q)) : options;
	}, [options, buscar]);

	const elegidas = options.filter(o => value.includes(o.id));
	const resumen = elegidas.length === 0 ? allText : elegidas.length === 1 ? elegidas[0].name : (feminine ? S.chosenF(elegidas.length) : S.chosen(elegidas.length));

	const alternar = (id: number) => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

	return (
		<div className={"avanceSelect avanceMulti" + (abierto ? " abierto" : "")} ref={caja}>
			<span>{label}</span>
			<button
				type="button"
				className={"avanceMultiBoton" + (elegidas.length ? " conValor" : "")}
				aria-haspopup="listbox"
				aria-expanded={abierto}
				disabled={disabled}
				onClick={() => setAbierto(a => !a)}
			>
				<span className="avanceMultiTexto">{resumen}</span>
				<FontAwesomeIcon icon={solid("chevron-down")} />
			</button>
			{
				!abierto ? null :
					<div className="avanceMultiLista" role="listbox" aria-multiselectable="true" aria-label={label}>
						{
							options.length < 8 ? null :
								<div className="avanceMultiBuscar">
									<FontAwesomeIcon icon={solid("magnifying-glass")} />
									<input type="search" value={buscar} placeholder={S.searchOptions} autoFocus={window.innerWidth > 700} onChange={e => setBuscar(e.target.value)} />
								</div>
						}
						<label className={"avanceMultiOpcion todos" + (value.length === 0 ? " activa" : "")}>
							<input type="checkbox" checked={value.length === 0} onChange={() => onChange([])} />
							<span>{S.all}</span>
						</label>
						{
							visibles.map(o => {
								const activa = value.includes(o.id);
								return (
									<label key={o.id} className={"avanceMultiOpcion" + (activa ? " activa" : "")}>
										<input type="checkbox" checked={activa} onChange={() => alternar(o.id)} />
										<span>{o.name}</span>
									</label>
								);
							})
						}
						{visibles.length === 0 ? <div className="avanceMultiVacio">{S.noMatches}</div> : null}
					</div>
			}
		</div>
	);
};

export default MultiSelect;
